import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    CAccordion,
    CAccordionBody,
    CAccordionHeader,
    CAccordionItem,
    CAlert,
    CCard,
    CCardBody,
    CCardHeader,
    CCol,
    CFormSelect,
    CRow,
    CSpinner,
    CTable,
    CTableBody,
    CTableHead,
    CTableHeaderCell,
    CTableDataCell,
    CTableRow,
} from '@coreui/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { apiClient } from 'services/Api';
import {
    GROUP_FIELDS,
    getGroupsForDepartment,
    formatScore,
    getAvailableYears,
} from './questions';

const ManagerOverview = ({ selectedYear: selectedYearProp, onYearChange }) => {
    const currentYear = new Date().getFullYear();
    const [selectedYearLocal, setSelectedYearLocal] = useState(currentYear);
    const isYearControlled = selectedYearProp != null;
    const selectedYear = selectedYearProp ?? selectedYearLocal;
    const [deptAverages, setDeptAverages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [allConcluded, setAllConcluded] = useState(false);

    const setSelectedYear = useCallback(
        (year) => {
            if (isYearControlled) {
                if (onYearChange) onYearChange(year);
            } else {
                setSelectedYearLocal(year);
            }
        },
        [isYearControlled, onYearChange]
    );

    const handleYearChange = useCallback(
        (event) => {
            const parsed = parseInt(event.target.value, 10);
            if (!Number.isFinite(parsed)) return;
            setSelectedYear(parsed);
        },
        [setSelectedYear]
    );

    // Stable array (avoid recreating every render)
    const availableYears = useMemo(() => getAvailableYears(2019), []);

    const fetchDeptAverages = useCallback(async (year) => {
        setLoading(true);
        try {
            const response = await apiClient.get(
                `/supervisory-assessments/averages/?year=${year}`
            );
            const payload = response.data || {};
            setDeptAverages(payload.departments || []);
            setAllConcluded(Boolean(payload.all_concluded));
        } catch (error) {
            console.error('Error fetching department supervisory averages:', error);
            setDeptAverages([]);
            setAllConcluded(false);
        } finally {
            setLoading(false);
        }
    }, []);

    const latestYear = availableYears[availableYears.length - 1];
    const effectiveYear = availableYears.includes(selectedYear)
        ? selectedYear
        : latestYear;

    useEffect(() => {
        // If somehow the selected year is outside the allowed range, correct it once
        if (selectedYear !== effectiveYear) {
            setSelectedYear(effectiveYear);
            return;
        }

        fetchDeptAverages(effectiveYear);
    }, [effectiveYear, selectedYear, fetchDeptAverages, setSelectedYear]);

    const allQuestionFields = useMemo(
        () => GROUP_FIELDS.reduce((acc, group) => acc.concat(group), []),
        []
    );

    const chartData = useMemo(() => {
        if (!allConcluded) {
            return {
                scatterPoints: [],
                averageLine: [],
                histogramCategories: [],
                histogramSeries: [],
                hasScatterData: false,
                hasHistogramData: false,
            };
        }

        const userMap = new Map();

        deptAverages.forEach((dept) => {
            const deptName = dept.department_name || '—';
            const evaluatees = Array.isArray(dept.evaluatees) ? dept.evaluatees : [];
            evaluatees.forEach((ev) => {
                const values = allQuestionFields
                    .map((field) => Number(ev[field]))
                    .filter((val) => Number.isFinite(val));
                if (values.length === 0) return;

                const avg = values.reduce((acc, v) => acc + v, 0) / values.length;
                const userId = ev.user_id;
                if (!userMap.has(userId)) {
                    userMap.set(userId, {
                        userId,
                        userName: ev.user_name || '—',
                        deptScores: [],
                    });
                }
                userMap.get(userId).deptScores.push({
                    department: deptName,
                    value: avg,
                });
            });
        });

        const orderedUsers = Array.from(userMap.values()).sort((a, b) =>
            (a.userName || '').localeCompare(b.userName || '')
        );

        if (orderedUsers.length === 0) {
            return {
                scatterPoints: [],
                averageLine: [],
                histogramCategories: [],
                histogramSeries: [],
                hasScatterData: false,
                hasHistogramData: false,
            };
        }

        const userCategories = orderedUsers.map((u) => u.userName);
        const scatterPoints = [];
        const averageLine = orderedUsers.map((user, userIndex) => {
            const values = user.deptScores.map((d) => d.value);
            if (values.length === 0) {
                return { x: userIndex, y: null, userLabel: user.userName };
            }

            values.forEach((value, idx) => {
                scatterPoints.push({
                    x: userIndex,
                    y: Number(value),
                    userLabel: user.userName,
                    department: user.deptScores[idx].department,
                });
            });

            const avg = values.reduce((acc, v) => acc + v, 0) / values.length;
            return { x: userIndex, y: Number(avg.toFixed(4)), userLabel: user.userName };
        });

        const histogramScores = orderedUsers
            .map((user) => {
                const values = user.deptScores.map((d) => d.value);
                if (values.length === 0) return null;
                return values.reduce((acc, v) => acc + v, 0) / values.length;
            })
            .filter((v) => Number.isFinite(v));

        const BIN_START = 1;
        const BIN_END = 5;
        const BIN_STEP = 0.4;
        const binCount = Math.round((BIN_END - BIN_START) / BIN_STEP);
        const histogramCategories = [];
        const histogramSeries = new Array(binCount).fill(0);

        for (let i = 0; i < binCount; i += 1) {
            const from = BIN_START + BIN_STEP * i;
            const to = from + BIN_STEP;
            histogramCategories.push(`${from.toFixed(1)}–${to.toFixed(1)}`);
        }

        const scaledStart = BIN_START * 100;
        const scaledStep = Math.round(BIN_STEP * 100);

        histogramScores.forEach((value) => {
            if (!Number.isFinite(value)) return;
            const clamped = Math.min(Math.max(value, BIN_START), BIN_END);
            const normalized = Math.round(clamped * 100) / 100;
            const scaledValue = Math.round(normalized * 100);
            const snapped =
                Math.round((scaledValue - scaledStart) / scaledStep) * scaledStep + scaledStart;
            let idx = Math.round((snapped - scaledStart) / scaledStep);
            if (idx < 0) idx = 0;
            if (idx >= binCount) idx = binCount - 1;
            histogramSeries[idx] += 1;
        });

        return {
            userCategories,
            scatterPoints,
            averageLine,
            histogramCategories,
            histogramSeries,
            hasScatterData: scatterPoints.length > 0,
            hasHistogramData: histogramScores.length > 0,
        };
    }, [allConcluded, deptAverages, allQuestionFields]);

    const scatterOptions = useMemo(() => {
        if (!chartData.hasScatterData) return null;

        return {
            chart: {
                type: 'scatter',
                height: 360,
            },
            title: {
                text: 'Avaliação por Responsável',
            },
            xAxis: {
                categories: chartData.userCategories,
                title: { text: 'Responsável' },
            },
            yAxis: {
                min: 1,
                max: 5,
                title: { text: 'Média por departamento' },
                tickInterval: 0.5,
            },
            tooltip: {
                useHTML: true,
                formatter() {
                    const label = this.point?.userLabel || this.series.name;
                    const value = typeof this.y === 'number' ? this.y.toFixed(2) : '—';
                    const prefix = this.series.type === 'line' ? 'Média' : 'Valor';
                    const dept = this.point?.department ? `<br/>${this.point.department}` : '';
                    return `<strong>${label}</strong>${dept}<br/>${prefix}: ${value}`;
                },
            },
            plotOptions: {
                scatter: {
                    marker: {
                        radius: 4,
                        symbol: 'circle',
                    },
                },
                line: {
                    marker: {
                        enabled: true,
                        radius: 3,
                    },
                },
            },
            legend: {
                enabled: true,
            },
            credits: { enabled: false },
            series: [
                {
                    type: 'scatter',
                    name: 'Colaboradores',
                    data: chartData.scatterPoints,
                    color: '#9e9e9e',
                    showInLegend: false,
                },
                {
                    type: 'line',
                    name: 'Média',
                    data: chartData.averageLine,
                    color: '#1f77b4',
                    connectNulls: true,
                },
            ],
        };
    }, [chartData]);

    const histogramOptions = useMemo(() => {
        if (!chartData.hasHistogramData) return null;

        return {
            chart: {
                type: 'column',
                height: 320,
            },
            title: {
                text: 'Avaliação por intervalos',
            },
            xAxis: {
                categories: chartData.histogramCategories,
                title: { text: 'Média global' },
                tickmarkPlacement: 'on',
            },
            yAxis: {
                title: { text: 'Nº de colaboradores' },
                allowDecimals: false,
            },
            tooltip: {
                pointFormat: '<strong>{point.y}</strong> colaboradores',
            },
            plotOptions: {
                column: {
                    pointPadding: 0.1,
                    groupPadding: 0.05,
                },
            },
            legend: {
                enabled: false,
            },
            credits: { enabled: false },
            series: [
                {
                    name: 'Colaboradores',
                    data: chartData.histogramSeries,
                    color: '#74c476',
                },
            ],
        };
    }, [chartData]);

    return (
        <CCard className="mb-4">
            <CCardHeader className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Visão Global por Departamento</h4>

                <CRow className="g-2 align-items-center">
                    <CCol xs="auto">
                        <strong>Ano:&nbsp;</strong>
                    </CCol>
                    <CCol xs="auto">
                        <CFormSelect
                            size="sm"
                            value={selectedYear}
                            onChange={handleYearChange}
                        >
                            {availableYears.length === 0 && (
                                <option value={selectedYear}>{selectedYear}</option>
                            )}
                            {availableYears.map((y) => (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            ))}
                        </CFormSelect>
                    </CCol>
                </CRow>
            </CCardHeader>

            <CCardBody>
                {loading ? (
                    <div className="text-center my-3">
                        <CSpinner size="sm" /> A carregar…
                    </div>
                ) : deptAverages.length === 0 ? (
                    <CAlert color="info">
                        Não existem resultados agregados para o ano seleccionado.
                    </CAlert>
                ) : (
                    <>
                        {allConcluded ? (
                            chartData.hasScatterData || chartData.hasHistogramData ? (
                                <CRow className="g-3 mb-4">
                                    {chartData.hasScatterData && (
                                        <CCol lg={12}>
                                            <CCard className="h-100">
                                                <CCardBody>
                                                    <HighchartsReact highcharts={Highcharts} options={scatterOptions} />
                                                </CCardBody>
                                            </CCard>
                                        </CCol>
                                    )}
                                    {chartData.hasHistogramData && (
                                        <CCol lg={12}>
                                            <CCard className="h-100">
                                                <CCardBody>
                                                    <HighchartsReact
                                                        highcharts={Highcharts}
                                                        options={histogramOptions}
                                                    />
                                                </CCardBody>
                                            </CCard>
                                        </CCol>
                                    )}
                                </CRow>
                            ) : (
                                <CAlert color="info" className="mb-4">
                                    Sem dados suficientes para apresentar os gráficos.
                                </CAlert>
                            )
                        ) : (
                            <CAlert color="warning" className="mb-4">
                                Os gráficos ficam disponíveis apenas quando todas as avaliações do ano estiverem concluídas.
                            </CAlert>
                        )}

                        <CAccordion alwaysOpen={false}>
                            {deptAverages.map((dept) => {
                                const deptName = dept.department_name || '';
                                const groups = getGroupsForDepartment(deptName);
                                const evaluatees = dept.evaluatees || []; // [{user_id, user_name, g1_1...}]

                                return (
                                    <CAccordionItem
                                        key={dept.department_id}
                                        itemKey={dept.department_id}
                                    >
                                        <CAccordionHeader>
                                            <strong>{deptName}</strong>
                                        </CAccordionHeader>

                                        <CAccordionBody>
                                            {groups.map((group, groupIndex) => (
                                                <CCard
                                                    className="mb-4"
                                                    key={`${dept.department_id}-${group.title}`}
                                                >
                                                    <CCardHeader>
                                                        <h5 className="mb-0">{group.title}</h5>
                                                    </CCardHeader>

                                                    <CCardBody>
                                                        <div className="table-responsive">
                                                            <CTable responsive small className="align-middle">
                                                                <CTableHead>
                                                                    <CTableRow>
                                                                        <CTableHeaderCell style={{ width: '40%' }} />
                                                                        {evaluatees.map((ev) => (
                                                                            <CTableHeaderCell
                                                                                key={ev.user_id}
                                                                                className="text-center"
                                                                            >
                                                                                {ev.user_name}
                                                                            </CTableHeaderCell>
                                                                        ))}
                                                                    </CTableRow>
                                                                </CTableHead>

                                                                <CTableBody>
                                                                    {group.questions.map((qText, qIndex) => {
                                                                        const fieldName =
                                                                            GROUP_FIELDS[groupIndex][qIndex];
                                                                        return (
                                                                            <CTableRow key={fieldName}>
                                                                                <CTableDataCell>{qText}</CTableDataCell>
                                                                                {evaluatees.map((ev) => (
                                                                                    <CTableDataCell
                                                                                        key={`${ev.user_id}-${fieldName}`}
                                                                                        className="text-center"
                                                                                    >
                                                                                        {formatScore(ev[fieldName])}
                                                                                    </CTableDataCell>
                                                                                ))}
                                                                            </CTableRow>
                                                                        );
                                                                    })}
                                                                </CTableBody>
                                                            </CTable>
                                                        </div>
                                                    </CCardBody>
                                                </CCard>
                                            ))}
                                        </CAccordionBody>
                                    </CAccordionItem>
                                );
                            })}
                        </CAccordion>
                    </>
                )}
            </CCardBody>
        </CCard>
    );
};

export default ManagerOverview;
